import React, { useMemo } from 'react';
import { Skeleton } from '@lognai/synapse-design-system-v2';
import type { Task, TaskStatus } from '../../types/tasks';
import { TASK_STATUSES, STATUS_LABELS } from '../../types/tasks';
import { TASK_STAGE_THEME } from './stage-theme';
import { TaskCard } from './TaskCard';
import { isExternallyManaged } from '../../utils/external-source';

export interface TaskBoardProps {
  tasks: Task[];
  isLoading?: boolean;
  onEditTask?: (task: Task) => void;
  onStatusChange?: (id: string, status: TaskStatus) => void;
  onOpenTask?: (task: Task) => void;
  isUpdating?: boolean;
  /** Current user id, used to decide which cards the user may move/edit. */
  currentUserId?: string | null;
  /** Whether the current user is an admin (may manage any task). */
  isAdmin?: boolean;
}

const canManageTask = (
  task: Task,
  currentUserId?: string | null,
  isAdmin?: boolean
): boolean =>
  !isExternallyManaged(task) &&
  (!!isAdmin ||
    (!!currentUserId && (currentUserId === task.assigneeUserId || currentUserId === task.createdBy)));

/** Status moves are assignee-only; creator/admin only when the task is unassigned. */
const canChangeStatusTask = (
  task: Task,
  currentUserId?: string | null,
  isAdmin?: boolean
): boolean => {
  if (isExternallyManaged(task)) return false;
  if (task.assigneeUserId) return !!currentUserId && currentUserId === task.assigneeUserId;
  return !!isAdmin || (!!currentUserId && currentUserId === task.createdBy);
};


const COLUMNS = TASK_STATUSES.map((status) => ({
  status,
  title: STATUS_LABELS[status],
  theme: TASK_STAGE_THEME[status],
}));

export const TaskBoard: React.FC<TaskBoardProps> = ({
  tasks,
  isLoading = false,
  onEditTask,
  onStatusChange,
  onOpenTask,
  isUpdating,
  currentUserId,
  isAdmin,
}) => {
  const grouped = useMemo(() => {
    const map = Object.fromEntries(TASK_STATUSES.map((s) => [s, [] as Task[]])) as Record<
      TaskStatus,
      Task[]
    >;
    for (const task of tasks) {
      if (map[task.status]) map[task.status].push(task);
    }
    return map;
  }, [tasks]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-3">
        {COLUMNS.map((c) => (
          <div key={c.status} className="overflow-hidden rounded-xl border border-border bg-surface">
            <div className={`flex items-center gap-2 px-4 py-3 ${c.theme.header}`}>
              <Skeleton className="h-2.5 w-2.5 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="space-y-3 p-3">
              <Skeleton className="h-28 w-full rounded-lg" />
              <Skeleton className="h-28 w-full rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-3">
      {COLUMNS.map((col) => {
        const colTasks = grouped[col.status];
        const isEmpty = colTasks.length === 0;
        return (
          <section
            key={col.status}
            className="flex flex-col overflow-hidden rounded-xl border border-border bg-surface"
          >
            {/* Status-tinted header band — the lane's identity at a glance. */}
            <header className={`flex items-center gap-2 px-4 py-3 ${col.theme.header}`}>
              <span className={`h-2.5 w-2.5 rounded-full ${col.theme.dot}`} />
              <h3 className={`text-sm font-semibold tracking-tight ${col.theme.title}`}>{col.title}</h3>
              <span
                className={`ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums ${
                  isEmpty ? 'bg-muted text-muted-foreground' : col.theme.count
                }`}
              >
                {colTasks.length}
              </span>
            </header>

            {/* Recessed trough so the white task cards lift off the lane. */}
            <div className="flex-1 p-2.5">
              {isEmpty ? (
                <div className="flex min-h-[7rem] items-center justify-center rounded-lg border border-dashed border-border/70 px-4 py-8 text-center text-sm text-muted-foreground">
                  No tasks here yet
                </div>
              ) : (
                colTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onEdit={onEditTask}
                    onStatusChange={onStatusChange}
                    onOpen={onOpenTask}
                    isUpdating={isUpdating}
                    canManage={canManageTask(task, currentUserId, isAdmin)}
                    canChangeStatus={canChangeStatusTask(task, currentUserId, isAdmin)}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
};
